import React from 'react';
import _ from 'lodash';
import { Button } from '@janus.team/janus-particles';
import { Container, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { useProductModelContext } from 'customer/product-model-context';
import { useUpdatedTo } from 'projects/project-selector/hooks';
import { getServiceBlockName, serviceLevelLabel } from 'projects/utils';
import { ActiveFiltersPills, ProjectFilterDrawer, emptySelectionFrom } from './project-filter';
import { projectsPageFilterContext } from './context';

const serviceBlocksFromProject = (products, project) => (
  _.some(project?.serviceBlocks)
    ? _.map(project?.serviceBlocks, (serviceBlock) => ({
      id: serviceBlock?.serviceBlockId,
      name: getServiceBlockName(products, serviceBlock.serviceBlockId),
    })) : [{ id: project?.serviceLevel, name: serviceLevelLabel(project?.serviceLevel) }]
);

const tally = (list) => _.reduce(list, (result, item) => _.assign(result, {
  [item?.id]: {
    count: _.get(result, [item?.id, 'count'], 0) + 1,
    id: item?.id,
    name: item?.name,
  },
}), {});

const usePotentialServiceBlocksOptions = (projects, products) => React.useMemo(
  () => tally(_.flatMap(projects, _.partial(serviceBlocksFromProject, products))), [projects, products],
);

const usePotentialStatusOptions = (projects) => React.useMemo(
  () => tally(_.map(projects, 'linkedState').map((status) => ({ id: status, name: _.startCase(_.lowerCase(status)) }))), [projects],
);

export const ProjectsToolBar = ({
  ActionButton, className, queryStringFilter, projects,
}) => {
  const { products } = useProductModelContext();
  const statusOptions = usePotentialStatusOptions(projects);
  const serviceBlockOptions = usePotentialServiceBlocksOptions(projects, products);

  const [value, setValue] = React.useState(''); // Search bar state
  const [filterState, setFilterState] = React.useState(_.merge(
    emptySelectionFrom({ serviceBlocks: _.keys(serviceBlockOptions), statuses: _.keys(statusOptions) }),
    queryStringFilter,
  ));

  const toFilterOpen = useUpdatedTo({ filterDrawer: 'open' });
  const setProjectsSearch = React.useContext(projectsPageFilterContext)?.setFilter;

  React.useEffect(() => {
    const timeoutId = global.setTimeout(() => setProjectsSearch(value), 100);
    return () => global.clearTimeout(timeoutId);
  }, [setProjectsSearch, value]);

  return (
    <Container className={`${className}`}>
      <Container className={`${className} d-flex justify-content-between flex-wrap-reverse flex-md-nowrap gap-6`}>
        { /* Search bar */ }
        <Container className="d-flex align-items-center">
          <Form
            id="list-global-search"
            onSubmit={(event) => event.preventDefault()}
            role="search"
            className="flex-fill position-relative"
          >
            <Form.Control
              className="flex-fill px-12"
              type="text"
              value={value}
              onChange={({ target: { value } }) => setValue(value)}
              aria-label="Search"
              placeholder="Search projects"
            />
            <i
              className="fa fa-lg fa-search position-absolute text-muted"
              style={{ top: '12px', left: '14px' }}
            />
          </Form>
        </Container>
        { /* Filter and `Action` buttons */ }
        <Container className="d-flex gap-4 pr-0 justify-content-start justify-content-md-end  align-items-center">
          <Button aria-label="Filter Projects" variant="outline-primary" as={Link} to={toFilterOpen}>
            <i className="fa fa-sliders" />
            {' '}
            Filters
          </Button>
          <ActionButton />
        </Container>
      </Container>
      { /* Active filters pills */ }
      <ActiveFiltersPills filterState={filterState} setFilterState={setFilterState} />
      <ProjectFilterDrawer
        filterState={filterState}
        setFilterState={setFilterState}
        projects={projects}
        serviceBlockOptions={serviceBlockOptions}
        statusOptions={statusOptions}
      />
    </Container>
  );
};
